import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  ClipboardCheck, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  Lightbulb,
  Target,
  Zap,
  ArrowRight,
  TrendingUp,
  Users,
  ChevronDown,
  ChevronUp,
  Star,
  Shield,
  MessageSquare
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  compliance: Shield,
  reputation: Star,
  operations: Target,
  marketing: TrendingUp,
  customer_service: MessageSquare,
  technology: Zap,
  legal: ClipboardCheck,
  financial: TrendingUp,
  hr: Users,
  other: Lightbulb
};

const categoryColors = {
  compliance: "bg-blue-100 text-blue-700 border-blue-200",
  reputation: "bg-yellow-100 text-yellow-700 border-yellow-200",
  operations: "bg-green-100 text-green-700 border-green-200",
  marketing: "bg-purple-100 text-purple-700 border-purple-200",
  customer_service: "bg-pink-100 text-pink-700 border-pink-200",
  technology: "bg-cyan-100 text-cyan-700 border-cyan-200",
  legal: "bg-red-100 text-red-700 border-red-200",
  financial: "bg-orange-100 text-orange-700 border-orange-200",
  hr: "bg-indigo-100 text-indigo-700 border-indigo-200",
  other: "bg-slate-100 text-slate-700 border-slate-200"
};

const urgencyColors = {
  low: "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700"
};

const priorityColors = {
  HIGH: "bg-red-500",
  MEDIUM: "bg-yellow-500",
  LOW: "bg-green-500"
};

export const ComplianceAnalyzer = () => {
  const [problem, setProblem] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showServices, setShowServices] = useState(false);

  const handleAnalyze = async () => {
    if (!problem.trim() || problem.length < 20) {
      alert("Please describe your problem in at least 20 characters");
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/analyze-problem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          problem: problem.trim(),
          business_name: businessName || null,
          contact_email: email || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        setResult({ error: "Analysis failed. Please try again." });
      }
    } catch (error) {
      console.error("Analysis error:", error);
      setResult({ error: "Unable to connect to analysis service." });
    }
    
    setLoading(false);
  };

  const CategoryIcon = result?.category ? categoryIcons[result.category] || Lightbulb : Lightbulb;

  return (
    <div className="w-full" data-testid="compliance-analyzer">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-1.5 rounded-full text-sm font-medium mb-3">
          <ClipboardCheck className="w-4 h-4" />
          FREE CONSULTATION
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Rate My Problem</h2>
        <p className="text-slate-600 max-w-xl mx-auto">
          Describe your business challenge and get AI-powered analysis with actionable recommendations
        </p>
      </div>

      {/* Input Form */}
      <Card className="border-slate-200 shadow-lg max-w-3xl mx-auto mb-6">
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              Describe Your Business Problem *
            </label>
            <Textarea
              placeholder="Example: We're receiving negative reviews about our response times. Customers are complaining that it takes too long to get support, and our ratings have dropped from 4.5 to 3.8 stars in the last month..."
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              className="min-h-[140px] text-base resize-none"
              data-testid="problem-input"
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">Be specific for better analysis</span>
              <span className={`text-xs ${problem.length < 20 ? "text-red-500" : "text-green-500"}`}>
                {problem.length}/20 min characters
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Business Name (Optional)
              </label>
              <Input
                placeholder="Your Company Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Email for Follow-up (Optional)
              </label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={loading || problem.length < 20}
            className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-base"
            data-testid="analyze-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Analyzing Your Problem...
              </>
            ) : (
              <>
                <Lightbulb className="w-5 h-5 mr-2" />
                Get Free Analysis
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && !result.error && (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="analysis-result">
          {/* Summary Card */}
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${categoryColors[result.category]}`}>
                    <CategoryIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <Badge className={categoryColors[result.category]}>
                      {result.category?.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <h3 className="text-lg font-bold text-slate-900 mt-1">Problem Analysis</h3>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-slate-500">Urgency:</span>
                    <Badge className={urgencyColors[result.urgency]}>
                      {result.urgency?.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Severity:</span>
                    <span className="text-xl font-bold text-slate-900">{result.severity_score}/10</span>
                  </div>
                </div>
              </div>

              {/* Diagnosis */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 mb-4">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4 text-purple-600" />
                  Diagnosis
                </h4>
                <p className="text-slate-700">{result.diagnosis}</p>
              </div>

              {/* Impact & Root Causes */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Root Causes
                  </h4>
                  <ul className="space-y-1">
                    {result.root_causes?.map((cause, idx) => (
                      <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                        {cause}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                  <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Impact Areas
                  </h4>
                  <ul className="space-y-1">
                    {result.impact_areas?.map((area, idx) => (
                      <li key={idx} className="text-sm text-orange-700 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-1.5 flex-shrink-0" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Recommendations
              </h4>
              <div className="space-y-3">
                {result.recommendations?.map((rec, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                  >
                    <div className={`w-2 h-2 rounded-full ${priorityColors[rec.priority]} mt-2`} />
                    <div className="flex-1">
                      <p className="text-slate-700">{rec.action}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {rec.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Wins */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6">
              <h4 className="font-bold text-green-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Quick Wins - Do These Today
              </h4>
              <div className="grid md:grid-cols-3 gap-3">
                {result.quick_wins?.map((win, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-green-200">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-xs text-green-600 font-medium">QUICK WIN</span>
                    </div>
                    <p className="text-sm text-slate-700">{win}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Estimated Improvement */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                <div>
                  <h4 className="font-semibold text-blue-900">Expected Improvement</h4>
                  <p className="text-blue-700">{result.estimated_improvement}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services We Can Help With */}
          <Card className="border-purple-200">
            <CardContent className="p-6">
              <button
                onClick={() => setShowServices(!showServices)}
                className="w-full flex items-center justify-between"
              >
                <h4 className="font-bold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  How We Can Help
                </h4>
                {showServices ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {showServices && (
                <div className="mt-4 space-y-3">
                  {result.relevant_services?.map((service, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-100"
                    >
                      <div>
                        <h5 className="font-semibold text-purple-900">{service.name}</h5>
                        <p className="text-sm text-purple-700">{service.description}</p>
                      </div>
                      <Button variant="outline" size="sm" className="border-purple-300 text-purple-700">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <p className="text-slate-600 mb-4">
              Want personalized help implementing these recommendations?
            </p>
            <Button className="bg-purple-600 hover:bg-purple-700">
              Schedule Free Consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Error State */}
      {result?.error && (
        <Card className="max-w-3xl mx-auto border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-700">{result.error}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ComplianceAnalyzer;
